import React, { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  CheckCircle2, Handshake, TrendingUp, ShieldCheck, BookOpen, Palette, Globe, Mail, Users,
  CreditCard, Wallet, Receipt, GitBranch, Wrench, Puzzle, Cpu, Gauge, LayoutDashboard, Sparkles,
} from "lucide-react";
import SEO from "@/components/SEO";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESELLER_FAQS = [
  {
    q: "Does PTT NZ offer a genuine wholesale MVNO model, or just a discount reseller scheme?",
    a: "A genuine wholesale MVNO platform. Resellers get their own white-label portal, Stripe Connect billing they control directly, a white-label APN you can set across every device — with APN assignment managed centrally through the portal, so deployments stay future-proofed — and native Android MDM. Not a simple discount on retail plans. Behind it, connectivity runs on native local network agreements — Spark NZ, One NZ and 2degrees in New Zealand, plus the Telstra Direct (Retail) network, Optus and Vodafone in Australia — not a wholesale roaming tier.",
  },
  {
    q: "Is there a minimum volume commitment to become a PTT NZ reseller?",
    a: "No fixed minimum. We look for NZ-registered businesses with an active customer base, relevant experience in telecoms, ICT, security or field-services, and a commitment to mission-critical service quality — volume grows from there.",
  },
  {
    q: "Can I fully white-label the platform under my own brand?",
    a: "Yes. Every reseller portal can be re-skinned with your own custom domain, logo, brand colours and transactional email — none of PTT NZ's branding is visible anywhere in your customers' journey. Sub-resellers get the same white-label toolkit for their own slice of the business.",
  },
  {
    q: "How does billing work — do I collect payment directly from my customers?",
    a: "Yes. Connect your own Stripe account and every sale is charged straight to your customer under your own business name, with your own invoices and receipts. You settle your wholesale costs with PTT NZ separately, on a fully prepaid basis, so there's no credit risk on either side.",
  },
  {
    q: "Can I build my own custom data plans as a reseller?",
    a: "Yes. The portal lets you build bespoke data plans and bundled services for your own customer base, priced and packaged your way. Your white-label APN is assigned and managed centrally in-portal; if you need a dedicated Private APN with static/dynamic IP or VPN isolation for a specific deployment, that's available as a separate custom-quoted service.",
  },
  {
    q: "Do sub-resellers get their own white-label tools too?",
    a: "Yes. Sub-resellers receive the same white-label toolkit to brand their own slice of the business, giving you a full multi-tier reseller network where each layer is invisible to the one below it.",
  },
];

const BENEFITS = [
  { icon: <TrendingUp size={20} />, title: "Strong reseller margins", text: "Competitive wholesale pricing on hardware, SIM plans and fibre." },
  { icon: <ShieldCheck size={20} />, title: "Mission-critical SLA", text: "Network-redundant SIMs and NZ-based escalation paths." },
  { icon: <Handshake size={20} />, title: "Sales & tech support", text: "Pre-sales engineering, demo units and joint deployment support." },
  { icon: <BookOpen size={20} />, title: "Reseller training", text: "Product training plus deal-registration to protect your pipeline." },
];

const WHITE_LABEL_POINTS = [
  { icon: <Globe size={18} />, title: "Your own custom domain", text: "Point a CNAME at your brand's domain and we auto-provision HTTPS — customers never see \"pttnz.net\"." },
  { icon: <Palette size={18} />, title: "Logo, tagline & brand colour", text: "Every dashboard, login screen and button is skinned in your identity, not ours." },
  { icon: <Mail size={18} />, title: "Branded transactional email", text: "Password resets, invoices and alerts are sent from your own address, in your own name." },
  { icon: <Users size={18} />, title: "Sub-resellers, white-labelled too", text: "Your sub-resellers get the same white-label toolkit to brand their own slice of the business — a full multi-tier reseller network, each layer invisible to the one below it." },
];

const PLATFORM_PILLARS = [
  { icon: <Puzzle size={20} />, title: "Built in-house, from the ground up", text: "PTT NZ engineered this entire platform ourselves — not a rebrand of someone else's system — so new feature ideas get built in-house, fast." },
  { icon: <Wrench size={20} />, title: "White-label APN, portal-managed", text: "Every reseller is provided a white-label APN that can be set across your whole device fleet — APN assignment is handled centrally through the portal, so future deployments and device swaps just work." },
  { icon: <Sparkles size={20} />, title: "Custom plans & services", text: "Build bespoke data plans and bundled services for your own customer base, priced and packaged your way." },
  { icon: <Cpu size={20} />, title: "Native Android MDM, built in", text: "Android Enterprise device management is native to the platform — zero-touch enrollment, remote lock & wipe, kiosk mode and more, no separate MDM login required." },
];

const SELF_SERVICE_POINTS = [
  { icon: <LayoutDashboard size={18} />, title: "Real-time SIM fleet dashboard", text: "Live totals for active, online and total data usage across every SIM and eSIM." },
  { icon: <Gauge size={18} />, title: "Network diagnostics tools", text: "Built-in network engineering diagnostics for teams who want to manage and troubleshoot their own devices." },
  { icon: <CheckCircle2 size={18} />, title: "Attach, order & manage SIMs", text: "Attach new SIMs, order stock and assign plans yourself, without waiting on a support queue." },
];

const initialForm = { company: "", contact_name: "", email: "", phone: "", region: "", business_type: "", monthly_volume: "", notes: "", website: "" };

const Resellers = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [formLoadedAt] = useState(() => Date.now());

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.email) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/resellers`, { ...form, form_loaded_at: formLoadedAt });
      toast.success("Application received. We'll be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error(err.response.data?.detail || "Too many submissions. Please try again later.");
      } else {
        toast.error("Something went wrong. Please email us directly.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="resellers-page">
      <SEO
        path="/resellers"
        title="Wholesale MVNO Provider NZ & AU — SIM Reseller"
        description="PTT NZ is a wholesale MVNO — resell NZ & Australia's multi-network SIM, IoT and Data Pool platform under your own white-label brand, with Stripe billing."
        keywords="wholesale MVNO provider New Zealand, wholesale MVNO Australia, white label MVNO NZ, SIM reseller New Zealand, white label telecom platform NZ, white label SIM reseller Australia, multi-network SIM reseller Australia, become a mobile reseller NZ, enterprise SIM reseller NZ AU, telecommunications reseller programme New Zealand, eSIM reseller Australia, sub reseller telecom platform, Stripe Connect telecom billing, custom APN portal, build your own mobile network NZ, MVNE New Zealand"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Wholesale white-label MVNO platform",
            provider: { "@type": "Organization", name: "PTT NZ Limited", url: "https://pttnz.net" },
            areaServed: ["New Zealand", "Australia"],
            name: "PTT NZ Wholesale MVNO / White-Label Reseller Platform",
            description: "A wholesale MVNO provider offering a 100% white-label multi-network SIM, IoT, Data Pool, PTT radio and body camera platform for resellers and businesses to build their own mobile network product on.",
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: RESELLER_FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          },
        ]}
      />
      <Toaster position="top-right" richColors />
      <section className="bg-[var(--ptt-paper)] border-b border-[var(--ptt-line)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 pb-12">
          <div className="eyebrow">Wholesale MVNO · Partner programme</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 leading-[1.02] max-w-3xl">
            A 100% white-label MVNO platform <span className="text-[var(--ptt-teal-deep)]">you can call your own.</span>
          </h1>
          <p className="text-[var(--ptt-muted)] mt-5 max-w-2xl">PTT NZ is a wholesale MVNO provider across New Zealand and Australia — resell our multi-network SIM, IoT, Data Pool, PTT radio, body camera and business fibre platform, or build your own mobile network product on top of it. Under your own brand, your own domain, your own Stripe billing. Sub-resellers welcome.</p>
        </div>
      </section>

      {/* PLATFORM PILLARS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-b border-[var(--ptt-line)]">
        <div className="eyebrow">Our platform</div>
        <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Built in-house. Endless flexibility.</h2>
        <p className="text-[var(--ptt-muted)] mt-4 max-w-2xl leading-relaxed">PTT NZ engineered this entire connectivity platform ourselves, from the ground up — which means custom feature ideas, integrations and network configuration all happen in-house, fast, instead of waiting on a third-party vendor.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="reseller-platform-pillars">
          {PLATFORM_PILLARS.map((p) => (
            <div key={p.title} className="bg-white border border-[var(--ptt-line)] p-6 hover:border-[var(--ptt-ink)] transition-colors" data-testid={`platform-pillar-${p.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
              <div className="w-11 h-11 grid place-items-center bg-[var(--ptt-teal)]/15">{p.icon}</div>
              <h3 className="font-display text-base font-bold mt-5 leading-snug">{p.title}</h3>
              <p className="text-sm text-[var(--ptt-muted)] mt-2 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHITE LABEL PLATFORM */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-b border-[var(--ptt-line)]">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow">White-label platform</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05]">Your customers only ever see <span className="text-[var(--ptt-teal-deep)]">your</span> brand.</h2>
            <p className="text-[var(--ptt-muted)] mt-4 leading-relaxed">Every reseller portal can be fully re-skinned — custom domain, logo, colours and transactional email — with none of PTT NZ&apos;s branding visible anywhere in the customer journey.</p>
            <div className="mt-7 space-y-5" data-testid="white-label-points">
              {WHITE_LABEL_POINTS.map((w) => (
                <div key={w.title} className="flex gap-3.5" data-testid={`white-label-point-${w.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                  <div className="w-9 h-9 shrink-0 grid place-items-center bg-[var(--ptt-teal)]/15 mt-0.5">{w.icon}</div>
                  <div>
                    <h3 className="font-display text-sm font-bold">{w.title}</h3>
                    <p className="text-sm text-[var(--ptt-muted)] mt-1 leading-relaxed">{w.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 space-y-5">
            <PortalShot src="/assets/portal-reseller-branding.webp" alt="White-label branding settings inside the PTT NZ reseller console — custom domain, logo, colours and transactional email" testId="shot-white-label-branding" />
            <PortalShot src="/assets/portal-reseller-overview.webp" alt="Reseller console overview showing customers, allocated SIMs, billing and sub-reseller visibility" testId="shot-reseller-overview" />
          </div>
        </div>
      </section>

      {/* STRIPE CONNECT BILLING */}
      <section className="bg-[var(--ptt-ink)] text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20">
          <div className="eyebrow text-white/50">Billing, your way</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05] max-w-2xl">Stripe Connect — you collect <span className="text-[var(--ptt-teal)]">100%</span> of every sale.</h2>
          <p className="text-white/70 mt-4 max-w-2xl leading-relaxed">
            Connect your own Stripe account and every plan or product you sell is charged straight to your customer under your own business name — invoices, receipts and transaction details included. You then settle your wholesale costs with PTT NZ separately, on your own schedule. Every plan and product across the platform — including everything sold through resellers and sub-resellers — runs on a fully prepaid basis, so there&apos;s no credit risk on either side.
          </p>

          <div className="mt-10 border border-white/15 bg-white/[0.03] p-6 lg:p-10 overflow-x-auto" data-testid="stripe-connect-diagram">
            <div className="flex items-center gap-3 lg:gap-5 min-w-[720px]">
              <div className="flex-1 border border-white/15 p-5 text-center">
                <Wallet size={24} className="mx-auto text-[var(--ptt-teal)]" />
                <div className="font-display text-sm font-bold mt-3">Your customer pays</div>
                <div className="text-[11px] text-white/60 mt-1">Checkout branded entirely as you</div>
              </div>
              <span className="text-white/40 shrink-0">→</span>
              <div className="flex-1 border-2 border-[var(--ptt-teal)] bg-[var(--ptt-teal)]/[0.08] p-5 text-center relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--ptt-teal)] text-[var(--ptt-ink)] text-[9px] font-mono-tech uppercase tracking-widest px-2 py-1 whitespace-nowrap">100% collected</div>
                <CreditCard size={24} className="mx-auto text-[var(--ptt-teal)] mt-1" />
                <div className="font-display text-sm font-bold mt-3">Your Stripe Connect account</div>
                <div className="text-[11px] text-white/60 mt-1">Full funds, your invoices &amp; receipts</div>
              </div>
              <span className="text-white/40 shrink-0">→</span>
              <div className="flex-1 border border-white/15 p-5 text-center">
                <Receipt size={24} className="mx-auto text-[var(--ptt-teal)]" />
                <div className="font-display text-sm font-bold mt-3">You settle with PTT NZ</div>
                <div className="text-[11px] text-white/60 mt-1">Wholesale cost, prepaid &amp; on your schedule</div>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8" data-testid="stripe-connect-features">
            {[
              { icon: <CreditCard size={16} />, text: "Customer receipts show only your brand" },
              { icon: <Wallet size={16} />, text: "Funds settle per Stripe's standard payout schedule" },
              { icon: <Receipt size={16} />, text: "Wholesale settlement kept separate — fully prepaid, no credit risk" },
              { icon: <GitBranch size={16} />, text: "Sub-resellers run their own Stripe Connect too" },
            ].map((f) => (
              <div key={f.text} className="border border-white/15 p-4 flex items-start gap-2.5 text-sm text-white/85">
                <span className="text-[var(--ptt-teal)] shrink-0 mt-0.5">{f.icon}</span>{f.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SELF SERVICE / DIAGNOSTICS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-b border-[var(--ptt-line)]">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:order-2">
            <div className="eyebrow">Self-service</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-[1.05]">Hands-on tools for customers and resellers alike.</h2>
            <p className="text-[var(--ptt-muted)] mt-4 leading-relaxed">Both your customers and your reseller team get a full self-service portal — live SIM fleet visibility plus network engineering diagnostic tools for anyone who wants to manage and troubleshoot their own devices.</p>
            <div className="mt-7 space-y-5" data-testid="self-service-points">
              {SELF_SERVICE_POINTS.map((s) => (
                <div key={s.title} className="flex gap-3.5" data-testid={`self-service-point-${s.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                  <div className="w-9 h-9 shrink-0 grid place-items-center bg-[var(--ptt-teal)]/15 mt-0.5">{s.icon}</div>
                  <div>
                    <h3 className="font-display text-sm font-bold">{s.title}</h3>
                    <p className="text-sm text-[var(--ptt-muted)] mt-1 leading-relaxed">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 lg:order-1 space-y-5">
            <PortalShot src="/assets/portal-customer-dashboard.webp" alt="Customer self-service dashboard with real-time SIM fleet overview and data usage trend" testId="shot-customer-dashboard" />
            <PortalShot src="/assets/portal-customer-sims.webp" alt="Customer self-service SIM management table, ICCID and MSISDN details blurred for privacy" testId="shot-customer-sims" />
          </div>
        </div>
      </section>

      {/* RESELLER FAQ */}
      <section className="max-w-4xl mx-auto px-5 lg:px-10 py-16 lg:py-20 border-b border-[var(--ptt-line)]" data-testid="reseller-faq-section">
        <div className="eyebrow">FAQ</div>
        <h2 className="font-display text-3xl lg:text-4xl font-bold mt-2 leading-tight">Wholesale &amp; reseller terms.</h2>
        <Accordion type="single" collapsible className="mt-8" data-testid="reseller-faq-accordion">
          {RESELLER_FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`reseller-faq-${i}`} className="border-[var(--ptt-line)]" data-testid={`reseller-faq-item-${i}`}>
              <AccordionTrigger data-testid={`reseller-faq-trigger-${i}`} className="font-display text-base font-bold text-left hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent data-testid={`reseller-faq-content-${i}`} className="text-[var(--ptt-muted)] leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="grid sm:grid-cols-2 gap-5" data-testid="reseller-benefits">
            {BENEFITS.map((b) => (
              <div key={b.title} className="bg-white border border-[var(--ptt-line)] p-5">
                <div className="w-10 h-10 grid place-items-center bg-[var(--ptt-teal)]/15">{b.icon}</div>
                <h3 className="font-display text-base font-bold mt-4">{b.title}</h3>
                <p className="text-xs text-[var(--ptt-muted)] mt-1.5 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 p-6 bg-[var(--ptt-ink)] text-white border border-[var(--ptt-ink)]">
            <div className="eyebrow text-white/50">What we look for</div>
            <ul className="mt-4 space-y-2 text-sm">
              {["NZ-registered business with active customer base", "Experience in telecoms, ICT, security or field-services", "Commitment to mission-critical service quality"].map((x) => (
                <li key={x} className="flex items-start gap-2"><CheckCircle2 size={14} className="text-[var(--ptt-teal)] mt-1 shrink-0" /> {x}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={onSubmit} data-testid="reseller-form" className="bg-white border border-[var(--ptt-ink)] p-6 lg:p-8">
            <h2 className="font-display text-2xl font-bold">Apply to become a reseller</h2>
            <p className="text-sm text-[var(--ptt-muted)] mt-1">Tell us about your business — we&apos;ll respond within two business days.</p>
            {/* Honeypot — hidden from real users, catches bots that auto-fill every field */}
            <div style={{ position: "absolute", left: "-9999px", top: "-9999px", opacity: 0, height: 0, width: 0, overflow: "hidden" }} aria-hidden="true">
              <label htmlFor="reseller-website">Website</label>
              <input
                id="reseller-website"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={onChange}
                data-testid="reseller-honeypot"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <Field label="Company *" name="company" value={form.company} onChange={onChange} testId="reseller-company" required />
              <Field label="Contact name *" name="contact_name" value={form.contact_name} onChange={onChange} testId="reseller-contact-name" required />
              <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} testId="reseller-email" required />
              <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testId="reseller-phone" />
              <Field label="Region" name="region" value={form.region} onChange={onChange} testId="reseller-region" placeholder="e.g. Canterbury" />
              <Field label="Business type" name="business_type" value={form.business_type} onChange={onChange} testId="reseller-business-type" placeholder="ICT, security, telco…" />
              <Field label="Estimated monthly volume" name="monthly_volume" value={form.monthly_volume} onChange={onChange} testId="reseller-volume" placeholder="SIMs / devices per month" />
            </div>
            <div className="mt-4">
              <label className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">Tell us more</label>
              <textarea name="notes" value={form.notes} onChange={onChange} rows={4} data-testid="reseller-notes" className="mt-1.5 w-full border border-[var(--ptt-line)] focus:border-[var(--ptt-ink)] outline-none px-3 py-2.5 text-sm" placeholder="Customers you'd deploy with, products you're interested in…" />
            </div>
            <button type="submit" disabled={submitting} data-testid="reseller-submit" className="ptt-button mt-6 px-6 py-3 text-sm font-bold disabled:opacity-60">
              {submitting ? "Submitting…" : "Submit application"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = "text", testId, placeholder, required }) => (
  <div>
    <label className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">{label}</label>
    <input
      data-testid={testId}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="mt-1.5 w-full border border-[var(--ptt-line)] focus:border-[var(--ptt-ink)] outline-none px-3 py-2.5 text-sm"
    />
  </div>
);

const PortalShot = ({ src, alt, testId }) => (
  <div className="border border-[var(--ptt-line)] bg-white p-1.5 shadow-[6px_6px_0_var(--ptt-ink)]" data-testid={testId}>
    <img src={src} alt={alt} className="w-full h-auto block" loading="lazy" />
  </div>
);

export default Resellers;
